import type { ReactNode } from 'react';
import { useWhere } from '../lib/roster';
import { formatAge } from '../lib/time';
import { EmptyState, Panel } from './ui';
import { ShowOnMap, characterSpot } from './ShowOnMap';

const NoData = () => <span className="text-muted">no data</span>;

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-muted">{label}</dt>
      <dd className="m-0 min-w-0">{children}</dd>
    </>
  );
}

function yesNo(v: boolean | null, yes: string, no: string) {
  if (v === null) return <NoData />;
  return v ? (
    <span className="font-mono text-accent">{yes}</span>
  ) : (
    <span className="text-muted">{no}</span>
  );
}

/** Where was I (SPEC §6.5): a character's re-entry card. */
export function WhereWasIPanel({
  characterId,
  note,
}: {
  characterId: string | null;
  note?: string;
}) {
  const q = useWhere(characterId);
  const w = q.data;
  return (
    <Panel
      title="Where was I"
      level="side"
      meta={
        w ? `${w.name} · ${w.lastLogoutAt ? formatAge(w.lastLogoutAt) : 'not played'}` : undefined
      }
    >
      {!characterId ? (
        <EmptyState>Nobody in tonight’s pool yet.</EmptyState>
      ) : !w ? (
        <EmptyState>{q.isError ? 'Couldn’t load it.' : 'Loading…'}</EmptyState>
      ) : (
        <>
          {note && <p className="m-0 px-4 pt-3 font-mono text-label text-muted">{note}</p>}
          <dl className="m-0 grid grid-cols-[max-content_minmax(0,1fr)] gap-x-3.5 gap-y-1.5 px-4 py-3 text-compact">
            <Row label="Zone">
              {w.zone ? (
                <>
                  {w.zone}
                  {w.subzone ? ` · ${w.subzone}` : ''}{' '}
                  <ShowOnMap
                    link={characterSpot(w.characterId, w.zone)}
                    character={w.characterId}
                  />
                </>
              ) : (
                <NoData />
              )}
            </Row>
            <Row label="Logout">
              {w.resting === null ? (
                <NoData />
              ) : w.resting ? (
                'resting · full-rate rested'
              ) : (
                'not resting'
              )}
            </Row>
            <Row label="Hearth">{w.hearth ?? <NoData />}</Row>
            <Row label="Bags">
              {w.freeBagSlots === null ? (
                <NoData />
              ) : (
                <span className="font-mono">{w.freeBagSlots} free</span>
              )}
            </Row>
            <Row label="Quests">
              {w.quests === null ? (
                <NoData />
              ) : w.quests.length === 0 ? (
                <span className="text-muted">none</span>
              ) : (
                <span className="flex flex-col gap-0.5">
                  {w.quests.map((q) => (
                    <span key={q.title}>
                      {q.title}{' '}
                      <span className={`font-mono ${q.ready ? 'text-accent' : 'text-muted'}`}>
                        {q.ready ? 'READY' : (q.progress ?? '')}
                      </span>
                    </span>
                  ))}
                </span>
              )}
            </Row>
            <Row label="Talents">
              {w.talentsUnspent === null ? (
                <NoData />
              ) : w.talentsUnspent > 0 ? (
                <span className="font-mono text-accent">{w.talentsUnspent} UNSPENT</span>
              ) : (
                <span className="text-muted">all spent</span>
              )}
            </Row>
            <Row label="Trainer">{yesNo(w.trainerDue, 'VISIT DUE', 'not due')}</Row>
            <Row label="Mail">{yesNo(w.mailWaiting, 'WAITING', 'none')}</Row>
            <Row label="Last">
              {w.recent.length === 0 ? (
                <span className="text-muted">nothing yet</span>
              ) : (
                <span className="flex flex-col gap-0.5">
                  {w.recent.map((e) => (
                    <span key={`${e.type}${e.occurredAt}${e.text}`}>{e.text}</span>
                  ))}
                </span>
              )}
            </Row>
          </dl>
        </>
      )}
    </Panel>
  );
}
